import { Box, Grid } from '@mui/material';
import { TabValue } from 'spw-esa-projects/building-details/occupancy-and-utilization/types';
import { SeatsSummary } from 'spw-redux/modules/ou/types';
import { ALL_CATEGORIES } from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/constants';
import { colors } from 'spw-theme';
import { useSelector } from 'react-redux';
import {
  currentProject,
  projectColors as selectProjectColors,
} from 'spw-redux/modules/projects/selectors';
import {
  InsightsCardSubTitle,
  InsightsCardRow,
  InsightsValue,
  InsightsMetric,
  Legend,
} from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/styles';
import React, { useMemo } from 'react';
import {
  asPercentageRounded,
  fixedNumberWithCommas,
  getProjectSpaceTypeCategoryByCategory,
  numberWithCommasOrDash,
} from 'spw-shared/utils';
import { Category } from 'spw-redux/modules/space_types/types';
import { ColorDot } from 'spw-esa-projects/building-details/occupancy-and-utilization/styles';

type Props = {
  category: TabValue;
  seatsSummary: SeatsSummary;
};

const CategorySeatsSummary = ({ category, seatsSummary }: Props) => {
  const { spaceTypeCategories } = useSelector(currentProject);
  const projectColors = useSelector(selectProjectColors);
  const areAllCategoriesSelected = category === ALL_CATEGORIES;
  const subtitleText = useMemo(() => {
    if (areAllCategoriesSelected) return 'All Seats';

    return getProjectSpaceTypeCategoryByCategory(
      spaceTypeCategories,
      category as Category
    ).name;
  }, [areAllCategoriesSelected, category, spaceTypeCategories]);

  const color = areAllCategoriesSelected
    ? colors.mainGray
    : (projectColors[category] as string);

  const valueColumnSize = areAllCategoriesSelected ? 6 : 12;

  return (
    <>
      <Box
        style={
          areAllCategoriesSelected
            ? { borderRight: `solid 1px ${colors.veryLightGray}` }
            : {}
        }
      >
        <Grid container>
          <Grid item xs={9}>
            <InsightsCardSubTitle>{subtitleText}</InsightsCardSubTitle>
          </Grid>
          <Grid item xs={3}>
            <Grid container style={{ height: '100%' }}>
              <Grid item xs={valueColumnSize}>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  height="100%"
                >
                  <ColorDot color={color} />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <InsightsCardRow>
          <Grid container>
            <Grid item xs={9}>
              <InsightsMetric>Existing Seats *</InsightsMetric>
            </Grid>
            <Grid item xs={3}>
              <Grid container>
                <Grid item xs={valueColumnSize}>
                  <InsightsValue>
                    {numberWithCommasOrDash(seatsSummary?.existing)}
                  </InsightsValue>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </InsightsCardRow>
        <InsightsCardRow>
          <Grid container>
            <Grid item xs={9}>
              <InsightsMetric>Observed Seats</InsightsMetric>
            </Grid>
            <Grid item xs={3}>
              <Grid container>
                <Grid item xs={valueColumnSize}>
                  <InsightsValue>
                    {numberWithCommasOrDash(seatsSummary?.observed)}
                  </InsightsValue>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </InsightsCardRow>
        <InsightsCardRow>
          <Grid container>
            <Grid item xs={9}>
              <InsightsMetric>Observed Seats In Use</InsightsMetric>
            </Grid>
            <Grid item xs={3}>
              <Grid container>
                <Grid item xs={valueColumnSize}>
                  <InsightsValue>
                    {numberWithCommasOrDash(seatsSummary?.inUse)}
                  </InsightsValue>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </InsightsCardRow>
        <InsightsCardRow>
          <Grid container>
            <Grid item xs={9}>
              <InsightsMetric>Average Seat Utilization</InsightsMetric>
            </Grid>
            <Grid item xs={3}>
              <Grid container>
                <Grid item xs={valueColumnSize}>
                  <InsightsValue>
                    {seatsSummary?.averageUtilization === undefined
                      ? '--'
                      : asPercentageRounded(seatsSummary?.averageUtilization)}
                  </InsightsValue>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </InsightsCardRow>
        <InsightsCardRow>
          <Grid container>
            <Grid item xs={9}>
              <InsightsMetric>Average Seats Used Per Space</InsightsMetric>
            </Grid>
            <Grid item xs={3}>
              <Grid container>
                <Grid item xs={valueColumnSize}>
                  <InsightsValue>
                    {seatsSummary?.averageSeatsUsedPerSpace !== undefined
                      ? fixedNumberWithCommas(
                          seatsSummary?.averageSeatsUsedPerSpace,
                          1
                        )
                      : '--'}
                  </InsightsValue>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </InsightsCardRow>
      </Box>
      <Legend>
        * Unobserved Spaces and Seats are excluded from calculations
      </Legend>
    </>
  );
};

export default CategorySeatsSummary;
